import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  ChevronLeft, ChevronRight,
  CheckCircle, Clock, XCircle, RefreshCw, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { logsServices } from '@/services/logs.service';
import { queryKeys } from '@/services/queryKeyFactory';
import DashboardError from '../error/DashboardError';

const RunLogs = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;


  const getStatusBadge = (status) => {
    switch (status) {
      case 200:
        return (
          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
            <CheckCircle className="w-3 h-3 mr-1" />
            Success
          </Badge>
        );
      case 'Pending':
      case 'Running':
        return (
          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
            <Clock className="w-3 h-3 mr-1" />
            {status}
          </Badge>
        );
      case 400:
        return (
          <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
            <XCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const {
    data,
    isLoading,
    isError,
    isFetching,
  } = useQuery({
    queryKey: queryKeys.runLogs(currentPage),
    queryFn: () => logsServices.getRunLogs(currentPage, itemsPerPage),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000
  });

  const handleExport = () => {
    toast.success('Exporting analytics data...');
    // Implement actual export logic
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Pagination
  const logs = data?.runs || [];
  const runCount = data?.run_count || 0;
  const totalPages = Math.ceil(runCount / itemsPerPage);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading run logs...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return <DashboardError
      title='Unable to load Run logs'
      section='run logs'
    />
  }

  return (
    <div className="space-y-6 min-h-[75vh]" data-testid="run-logs-page">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Run Logs</h1>
          <p className="text-muted-foreground">View history of all CompSet analysis runs</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
            <RefreshCw className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Run Count: {runCount}</span>
          </div>
          {/* <Button variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export Analytics Data
          </Button> */}
          {/* <Button onClick={() => setRunModalStatus(true)} className="bg-primary" disabled={isFetching}>
            Run Now
          </Button> */}
        </div>
      </div>

      {/* Recent Activity Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>
            All CompSet analysis runs with their status and execution time
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>CompSet Name</TableHead>
                <TableHead>Run Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Executed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs?.map((log) => (
                <TableRow key={log?.id}>
                  <TableCell className="font-medium">{log?.compset_name}</TableCell>
                  <TableCell>{log?.run_type}</TableCell>
                  <TableCell>{getStatusBadge(log?.status)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(log?.executed_at)}
                  </TableCell>
                </TableRow>
              ))}
              {logs?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    No run logs found. Click "Run Now" to start your first analysis.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {logs?.length > 0 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <span className="text-sm text-muted-foreground">
                {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, logs?.length)} of {logs?.length}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                  <ChevronLeft className="w-4 h-4 -ml-2" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm px-2">
                  Page {currentPage} of {totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || logs.length === 0}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages || logs.length === 0}
                >
                  <ChevronRight className="w-4 h-4" />
                  <ChevronRight className="w-4 h-4 -ml-2" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* <RunCompsetModal
        open={runModalStatus}
        onClose={() => setRunModalStatus(!runModalStatus)}
      /> */}

    </div>
  );
};

export default RunLogs;
